import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { TiTick } from 'react-icons/ti';
import { FaCheckCircle } from 'react-icons/fa';
import axios from 'axios';

import { formatRut } from '../../utils/rut';

/**
 * Checkout de la INVITACIÓN pre-aprobada de Caja Los Andes (link ?inv=), AISLADO
 * del checkout que está bajo pentest (`ClaCheckout.page.tsx` + rutas /cla).
 *
 * Etapa 1 — formulario de inscripción completo: recicla los campos/propiedades
 * del CRM (form "Caja Los Andes — Inscripción completa"). Los datos van DIRECTO
 * al CRM vía form-submit (el carnet se sube antes a S3 vía form-upload). El RUT y
 * el correo salen de la invitación (server-side) y se muestran read-only.
 * Etapa 2 — pago: al enviar el formulario, arranca Webpay contra el endpoint
 * propio del flujo (`/cla-inv/checkout`), con su Transbank independiente.
 */

const CRM_BASE = process.env.REACT_APP_CRM_BASE || 'https://crm.agentsmind.ai/api';
const FORM_ID = '43140005-31cd-4f4d-9f11-cee47f4b9c54';

const PROGRAMA = 'Formación en IA y Productividad';
const COPAGO = '$15.000';
const REFERENCIA = '$560.000';

// Código de país del teléfono (E.164). Chile por defecto; el resto cubre LATAM + ES.
const DIAL_CODES = [
  { code: '+56', label: '🇨🇱 +56' },
  { code: '+54', label: '🇦🇷 +54' },
  { code: '+51', label: '🇵🇪 +51' },
  { code: '+57', label: '🇨🇴 +57' },
  { code: '+52', label: '🇲🇽 +52' },
  { code: '+591', label: '🇧🇴 +591' },
  { code: '+598', label: '🇺🇾 +598' },
  { code: '+34', label: '🇪🇸 +34' },
];

// Definición de los campos (reciclados del form CRM 43140005). El bloque de
// identidad (nombre/apellido/rut/correo/teléfono) se renderiza aparte para
// precargarlo y dejar RUT/correo read-only. Acá van del 6º en adelante.
type FieldDef = {
  key: string;
  label: string;
  kind: 'text' | 'date' | 'select' | 'file' | 'checkbox';
  required: boolean;
  options?: string[];
  showIf?: { key: string; equals: string };
  sendValue?: string; // para checkbox: valor que se envía al marcar
};

const FIELDS: FieldDef[] = [
  { key: 'fecha_de_nacimiento', label: 'Fecha de nacimiento', kind: 'date', required: true },
  {
    key: 'nacionalidad',
    label: '¿Cuál es tu nacionalidad?',
    kind: 'select',
    required: true,
    options: ['Chilena', 'Argentina', 'Boliviana', 'Colombiana', 'Cubana', 'Ecuatoriana', 'Española', 'Mexicana', 'Peruana', 'Venezolana', 'Otra'],
  },
  { key: 'otra_nacionalidad', label: 'Si marcaste "Otra", indica tu nacionalidad', kind: 'text', required: false, showIf: { key: 'nacionalidad', equals: 'Otra' } },
  {
    key: 'pais',
    label: 'País de residencia',
    kind: 'select',
    required: true,
    options: ['Chile', 'Argentina', 'Bolivia', 'Colombia', 'Cuba', 'Ecuador', 'España', 'México', 'Perú', 'Venezuela', 'Otro'],
  },
  { key: 'otro_pais_residencia', label: 'Si marcaste "Otro", indica tu país', kind: 'text', required: false, showIf: { key: 'pais', equals: 'Otro' } },
  {
    key: 'region_residencia',
    label: 'Región de residencia',
    kind: 'select',
    required: true,
    options: ['Arica y Parinacota', 'Tarapacá', 'Antofagasta', 'Atacama', 'Coquimbo', 'Valparaíso', 'Metropolitana de Santiago', 'Libertador General Bernardo O\'Higgins', 'Maule', 'Ñuble', 'Biobío', 'La Araucanía', 'Los Ríos', 'Los Lagos', 'Aysén del General Carlos Ibáñez del Campo', 'Magallanes y de la Antártica Chilena', 'No resido en Chile'],
  },
  { key: 'comuna', label: 'Comuna', kind: 'text', required: false },
  { key: 'carnet_frente', label: 'Carnet de identidad — frente', kind: 'file', required: true },
  { key: 'carnet_reverso', label: 'Carnet de identidad — reverso', kind: 'file', required: true },
  { key: 'con_que_genero_te_identificas_', label: '¿Con qué género te identificas?', kind: 'select', required: true, options: ['Mujer', 'Hombre', 'Otro', 'Prefiero no responder'] },
  {
    key: 'maximo_nivel_educacional',
    label: 'Nivel educacional formal más alto alcanzado',
    kind: 'select',
    required: true,
    options: ['Educación media científico-humanista', 'Educación media técnico-profesional', 'Instituto profesional o CFT', 'Universidad', 'Postgrado', 'Otro'],
  },
  { key: 'estado_nivel_educacional', label: 'Estado del nivel educacional seleccionado', kind: 'select', required: true, options: ['Completado', 'Cursando actualmente', 'Incompleto'] },
  {
    key: 'realizado_bootcamp',
    label: '¿Has realizado algún bootcamp, curso intensivo o capacitación relacionada?',
    kind: 'select',
    required: true,
    options: ['Sí, completé uno o más cursos, bootcamps o capacitaciones', 'Sí, estoy cursando actualmente uno', 'Sí, inicié uno, pero no lo terminé', 'No, no he realizado ninguno'],
  },
  {
    key: 'como_es_tu_situacion_laboral_actual_',
    label: '¿Cuál es tu situación laboral actual?',
    kind: 'select',
    required: true,
    options: ['Empleado/a o trabajador/a dependiente', 'Trabajador/a independiente o freelance', 'Desempleado/a, buscando trabajo', 'Desempleado/a, sin búsqueda activa de trabajo'],
  },
  { key: 'cla_especialidad_1', label: 'Especialidad — 1ª preferencia', kind: 'select', required: true, options: ['Automatización con IA', 'IA para Productividad Diaria', 'Data Analytics', 'IA para Marketing y Ventas'] },
  { key: 'cla_especialidad_2', label: 'Especialidad — 2ª preferencia', kind: 'select', required: true, options: ['Automatización con IA', 'IA para Productividad Diaria', 'Data Analytics', 'IA para Marketing y Ventas'] },
  { key: 'cla_horario_clases', label: '¿En qué horario podrías asistir a clases en vivo?', kind: 'select', required: true, options: ['19:00', '20:00', '21:00'] },
  { key: 'acceso_internet', label: '¿Cuentas con acceso a internet para participar en clases en vivo?', kind: 'select', required: true, options: ['Sí, tengo conexión estable', 'Sí, pero mi conexión es inestable', 'No tengo acceso regular a internet'] },
  { key: 'cla_como_te_enteraste', label: '¿Cómo te enteraste del curso?', kind: 'select', required: false, options: ['RRSS de CLA', 'Email de CLA', 'Web de CLA', 'RRSS de ADL', 'Email de ADL', 'Web de ADL', 'Referido'] },
  { key: 'autorizacion_contacto_datos', label: 'Autorizo el contacto y uso de mis datos personales para todos los fines asociados a este programa.', kind: 'checkbox', required: true, sendValue: 'Acepto' },
  { key: 'cla_autoriza_otros_programas', label: 'Autorizo que Desafío Latam me contacte para otros programas, iniciativas o futuras convocatorias.', kind: 'checkbox', required: false, sendValue: 'Sí' },
];

const field =
  'w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-[#1A73E8] focus:ring-2 focus:ring-[#1A73E8]/20';
const label = 'mb-1 block text-sm font-semibold text-gray-700';

// El CRM rechaza con 400 los archivos sobre 10MB (FILE_MAX_SIZE en
// form_submissions_controller#upload). Una foto de carnet de celular pasa fácil
// ese peso, y antes el error llegaba como "revisa tus datos": la persona no
// tenía forma de saber que el problema era la imagen (caso real: 6 intentos
// fallidos con un archivo de 14,2 MB). Ahora se avisa el límite en el campo y,
// si se excede, la imagen se reduce automáticamente antes de subirla.
// El programa exige ser mayor de edad. El campo de fecha de nacimiento no
// validaba nada: se registraron inscripciones con año 2018 (8 años) y hasta con
// fecha futura (2026), casi siempre por error de tipeo. Se acota el rango en el
// propio input y se vuelve a chequear al enviar.
const EDAD_MINIMA = 18;
const EDAD_MAXIMA = 100;
// Mismo texto en el campo, en el error del envío y en la respuesta del servidor:
// se dice el requisito, sin exponer la edad calculada.
const MSG_MAYOR_EDAD = 'Para postular al programa debes ser mayor de edad.';

const isoHaceAnios = (anios: number) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - anios);
  return d.toISOString().slice(0, 10);
};
const NAC_MAX = isoHaceAnios(EDAD_MINIMA); // nacer después de esto = menor de edad
const NAC_MIN = isoHaceAnios(EDAD_MAXIMA);

/** Edad cumplida a hoy, o null si la fecha no es válida. */
const edadCumplida = (iso: string): number | null => {
  const n = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(n.getTime())) return null;
  const hoy = new Date();
  let edad = hoy.getFullYear() - n.getFullYear();
  const m = hoy.getMonth() - n.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < n.getDate())) edad -= 1;
  return edad;
};

const MAX_FILE_MB = 10;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
const IMG_MAX_SIDE = 2000;
const IMG_QUALITY = 0.85;

const mb = (bytes: number) => (bytes / 1024 / 1024).toFixed(1);

/** Error de archivo: su mensaje se muestra tal cual al usuario. */
class ArchivoError extends Error {}

/** Redimensiona y recomprime una imagen a JPEG para que quepa en el límite. */
async function reducirImagen(file: File): Promise<File> {
  const bitmap = await createImageBitmap(file);
  const escala = Math.min(1, IMG_MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * escala));
  const h = Math.max(1, Math.round(bitmap.height * escala));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ArchivoError('No pudimos procesar la imagen en este navegador.');
  ctx.drawImage(bitmap, 0, 0, w, h);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', IMG_QUALITY));
  if (!blob) throw new ArchivoError('No pudimos procesar la imagen en este navegador.');
  return new File([blob], `${file.name.replace(/\.[^.]+$/, '')}.jpg`, { type: 'image/jpeg' });
}

/** Deja el archivo listo para subir, o lanza ArchivoError con una instrucción concreta. */
async function prepararArchivo(file: File, campo: string): Promise<File> {
  if (file.size <= MAX_FILE_BYTES) return file;

  if (!file.type.startsWith('image/')) {
    throw new ArchivoError(
      `El archivo de "${campo}" pesa ${mb(file.size)} MB y el máximo es ${MAX_FILE_MB} MB. Sube una versión más liviana (por ejemplo, una foto en vez del PDF).`,
    );
  }

  let reducida: File;
  try {
    reducida = await reducirImagen(file);
  } catch (e) {
    if (e instanceof ArchivoError) throw e;
    throw new ArchivoError(
      `No pudimos reducir la imagen de "${campo}" (${mb(file.size)} MB). Sube una foto más liviana, de hasta ${MAX_FILE_MB} MB.`,
    );
  }
  if (reducida.size > MAX_FILE_BYTES) {
    throw new ArchivoError(
      `La imagen de "${campo}" sigue pesando ${mb(reducida.size)} MB después de reducirla y el máximo es ${MAX_FILE_MB} MB. Sube una foto de menor resolución.`,
    );
  }
  return reducida;
}

export const ClaInvitacionCheckoutPage = () => {
  const [params] = useSearchParams();
  const inv = params.get('inv') || '';

  // 'sin_cupos' es distinto de 'invalid' a propósito: el link ES válido, lo que
  // se agotó es el programa. Decirle "tu enlace expiró" a alguien que lo recibió
  // ayer manda a soporte a alguien que no tiene nada que arreglar.
  const [status, setStatus] = useState<'loading' | 'ok' | 'invalid' | 'sin_cupos'>('loading');
  const [cuposMsg, setCuposMsg] = useState('');
  const [rut, setRut] = useState('');
  const [dialCode, setDialCode] = useState('+56');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<Record<string, any>>();

  const nacionalidad = watch('nacionalidad');
  const pais = watch('pais');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_API_BACKEND}/cla-inv/invitation`, {
          params: { inv },
        });
        if (!alive) return;
        // Cupos agotados: se avisa ACÁ, al cargar, y no al pagar. Si no, la
        // persona llena el formulario completo (carnet incluido) para recién
        // enterarse al final.
        if (data?.cupos_agotados) {
          setCuposMsg(data.cupos_mensaje || '');
          setStatus('sin_cupos');
          return;
        }
        if (data?.rut) {
          setRut(formatRut(data.rut));
          // Precarga best-effort del teléfono: separa el código de país si viene E.164.
          const dc = DIAL_CODES.find((d) => (data.phone || '').startsWith(d.code));
          if (dc) setDialCode(dc.code);
          const phoneNational = dc ? String(data.phone).slice(dc.code.length) : (data.phone || '');
          reset({ firstname: data.name || '', lastname: data.lastname || '', email: data.email || '', phone: phoneNational });
          setStatus('ok');
        } else {
          setStatus('invalid');
        }
      } catch {
        if (alive) setStatus('invalid');
      }
    })();
    return () => {
      alive = false;
    };
  }, [inv, reset]);

  const isVisible = (f: FieldDef) => {
    if (!f.showIf) return true;
    const v = f.key === 'otra_nacionalidad' ? nacionalidad : f.key === 'otro_pais_residencia' ? pais : watch(f.showIf.key);
    return v === f.showIf.equals;
  };

  const onSubmit = async (values: Record<string, any>) => {
    setErrorMsg(null);

    // Validación de reglas propias que react-hook-form no cubre solo.
    if (values.cla_especialidad_1 && values.cla_especialidad_1 === values.cla_especialidad_2) {
      setErrorMsg('Elige dos especialidades distintas para tu 1ª y 2ª preferencia.');
      return;
    }
    const digits = String(values.phone || '').replace(/\D/g, '');
    if (digits.length < 8) {
      setErrorMsg('Ingresa un teléfono válido.');
      return;
    }
    // Mayoría de edad: requisito del programa. Se revisa acá además del min/max
    // del input, porque el navegador no siempre bloquea una fecha tecleada.
    const edad = edadCumplida(String(values.fecha_de_nacimiento || ''));
    if (edad === null) {
      setErrorMsg('Revisa tu fecha de nacimiento: no es una fecha válida.');
      return;
    }
    if (edad < EDAD_MINIMA) {
      setErrorMsg(MSG_MAYOR_EDAD);
      return;
    }
    if (edad > EDAD_MAXIMA) {
      setErrorMsg('Revisa tu fecha de nacimiento.');
      return;
    }

    setLoading(true);
    try {
      // 1) Subir el carnet (frente + reverso) a S3 vía el CRM → devuelve URLs.
      //    Si la imagen supera el límite del CRM se reduce acá antes de subirla,
      //    y si aun así no cabe, prepararArchivo lanza un mensaje que dice
      //    exactamente qué archivo es y cuánto pesa.
      const fileUrls: Record<string, string> = {};
      for (const key of ['carnet_frente', 'carnet_reverso']) {
        const fileList = values[key] as FileList | undefined;
        const original = fileList && fileList[0];
        if (original) {
          const campo = FIELDS.find((f) => f.key === key)?.label || key;
          const file = await prepararArchivo(original, campo);
          const fd = new FormData();
          fd.append('file', file);
          const { data } = await axios.post(`${CRM_BASE}/form-upload/${FORM_ID}`, fd);
          fileUrls[key] = data.url;
        }
      }

      // 2) Armar el payload para el CRM (keys = keys del form CRM).
      const phoneE164 = `${dialCode}${digits}`;
      const rutClean = formatRut(rut).replace(/\./g, ''); // 12345678-9 (estándar ADL)
      const payload: Record<string, any> = {
        firstname: String(values.firstname || '').trim(),
        lastname: String(values.lastname || '').trim(),
        email: String(values.email || '').trim(),
        phone: phoneE164,
        rut: rutClean,
      };
      FIELDS.forEach((f) => {
        if (!isVisible(f)) return;
        if (f.kind === 'file') {
          if (fileUrls[f.key]) payload[f.key] = fileUrls[f.key];
        } else if (f.kind === 'checkbox') {
          if (values[f.key]) payload[f.key] = f.sendValue;
          else if (f.key === 'cla_autoriza_otros_programas') payload[f.key] = 'No';
        } else if (values[f.key] != null && values[f.key] !== '') {
          payload[f.key] = values[f.key];
        }
      });

      // 3) Enviar la inscripción al CRM. Va PRIMERO: si falla, no cobramos.
      await axios.post(`${CRM_BASE}/form-submit/${FORM_ID}`, payload, {
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      });

      // 4) Iniciar el pago (Webpay). El RUT lo resuelve el backend desde la invitación.
      const country = pais === 'Otro' ? String(values.otro_pais_residencia || 'Otro') : String(pais || 'Chile');
      const { data } = await axios.post(`${process.env.REACT_APP_API_BACKEND}/cla-inv/checkout`, {
        name: payload.firstname,
        lastname: payload.lastname,
        email: payload.email,
        country,
        phone: phoneE164,
        inv,
      });
      if (data?.url) {
        window.location.href = data.url; // Webpay (Transbank)
        return;
      }
      throw new Error('no url');
    } catch (e: unknown) {
      // Cupos agotados (409 con error='cupos_agotados'): no hay nada que
      // corregir, así que se cambia toda la pantalla en vez de pintar un error
      // rojo bajo el botón. Se distingue por el código y no por el 409 pelado,
      // que ya lo usa "este RUT ya tiene una inscripción pagada".
      if (axios.isAxiosError(e) && (e.response?.data as any)?.error === 'cupos_agotados') {
        setCuposMsg(((e.response?.data as any)?.message as string) || '');
        setStatus('sin_cupos');
        setLoading(false);
        return;
      }
      // Prioridad: (1) problema de archivo, con instrucción concreta; (2) el
      // mensaje que devuelve el backend (el CRM responde textos claros en
      // español, p. ej. "Archivo excede 10MB"); (3) genérico. Antes todo caía en
      // el genérico "revisa tus datos", que mandaba a corregir datos correctos.
      const backendMsg =
        axios.isAxiosError(e) && typeof (e.response?.data as any)?.error === 'string'
          ? ((e.response?.data as any).error as string)
          : null;
      // 409 = el RUT ya tiene una inscripción pagada: el texto viene del backend
      // en `message` y no hay nada que corregir, así que se muestra tal cual.
      const yaInscrito =
        axios.isAxiosError(e) && e.response?.status === 409
          ? ((e.response?.data as any)?.message as string | undefined)
          : null;
      const msg =
        e instanceof ArchivoError
          ? e.message
          : yaInscrito
            ? yaInscrito
            : axios.isAxiosError(e) && e.response?.status === 422
              ? 'Tu enlace de pago no es válido o expiró. Escríbenos para reenviártelo.'
              : backendMsg
                ? `${backendMsg}. Corrige eso e inténtalo de nuevo.`
                : 'No se pudo completar tu inscripción. Revisa tus datos e inténtalo de nuevo.';
      setErrorMsg(msg);
      setLoading(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#E8F1FF] to-[#F5F7FA] px-4">
        <div className="flex items-center gap-3 text-sm font-semibold text-[#0B1F3A]">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#1A73E8] border-t-transparent" />
          Cargando tu inscripción…
        </div>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#E8F1FF] to-[#F5F7FA] px-4">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-lg">
          <h1 className="text-lg font-bold text-[#0B1F3A]">Enlace de inscripción no válido</h1>
          <p className="mt-2 text-sm text-gray-500">
            Tu enlace no es válido o expiró. Escríbenos para reenviártelo.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'sin_cupos') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#E8F1FF] to-[#F5F7FA] px-4">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-lg">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-amber-100 ring-4 ring-amber-50">
            <svg className="h-7 w-7 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.36-1.86M17 20H7m10 0v-2c0-.66-.13-1.3-.36-1.86m0 0A5 5 0 0012 13a5 5 0 00-4.64 3.14M7 20H2v-2a3 3 0 015.36-1.86M7 20v-2c0-.66.13-1.3.36-1.86m0 0A5 5 0 0112 13m0-4a3 3 0 100-6 3 3 0 000 6z" />
            </svg>
          </span>
          <h1 className="mt-5 text-lg font-bold text-[#0B1F3A]">Se agotaron los cupos</h1>
          <p className="mt-2 text-sm text-gray-500">
            {cuposMsg || 'Se agotaron los cupos del programa. Te avisaremos cuando abra la próxima convocatoria.'}
          </p>
          <p className="mt-4 text-xs text-gray-400">
            Tu enlace sigue siendo válido: si se liberan cupos podrás completar tu inscripción con este mismo link.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#E8F1FF] to-[#F5F7FA] px-4 py-10">
      <div className="grid w-full max-w-5xl overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-xl lg:grid-cols-[0.85fr_1.15fr]">
        {/* Aside de marca */}
        <div className="relative overflow-hidden bg-[#0B1F3A] p-8 text-white lg:p-10">
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#1A73E8]/25 blur-3xl" aria-hidden />
          <div className="relative">
            <div className="mb-7 flex items-center gap-3">
              <img src={`${process.env.PUBLIC_URL}/logos/logo-adl-blanco.png`} alt="Desafío Latam" className="h-6 w-auto" />
              <span className="h-5 w-px bg-white/25" />
              <img src={`${process.env.PUBLIC_URL}/logos/caja-los-andes-wh.svg`} alt="Caja Los Andes" className="h-8 w-auto" />
            </div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFC857]">Inscripción · Caja Los Andes</p>
            <h1 className="mt-3 text-2xl font-extrabold leading-tight">{PROGRAMA}</h1>
            <p className="mt-2 text-sm text-white/70">Completa tu inscripción y asegura tu cupo con el copago.</p>

            <div className="mt-7 rounded-2xl bg-white/[0.07] p-5 ring-1 ring-white/10">
              <p className="text-xs uppercase tracking-wide text-white/50">Copago (pago único)</p>
              <div className="mt-1 flex items-end gap-2">
                <span className="text-3xl font-extrabold text-white">{COPAGO}</span>
                <span className="mb-1 text-sm text-white/50 line-through">{REFERENCIA}</span>
              </div>
              <p className="mt-1 text-xs text-[#00D69A]">Beneficio Caja Los Andes aplicado</p>
            </div>

            <ul className="mt-7 list-none space-y-2.5 text-sm text-white/80">
              <li className="flex items-start gap-2"><TiTick className="mt-0.5 h-4 w-4 shrink-0 text-[#00D69A]" /> 8 meses de formación en IA aplicada</li>
              <li className="flex items-start gap-2"><TiTick className="mt-0.5 h-4 w-4 shrink-0 text-[#00D69A]" /> Certificación Desafío Latam</li>
              <li className="flex items-start gap-2"><TiTick className="mt-0.5 h-4 w-4 shrink-0 text-[#00D69A]" /> Pago seguro con Webpay</li>
            </ul>
          </div>
        </div>

        {/* Formulario */}
        <div className="p-8 lg:p-10">
          <h2 className="text-lg font-bold text-[#0B1F3A]">Completa tu inscripción</h2>
          <p className="mt-1 text-sm text-gray-500">Tus datos van directo a tu ficha. Luego pasarás al pago del copago.</p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
            {/* Identidad (precargada; RUT y correo read-only) */}
            <div>
              <label className={label}>RUT afiliado</label>
              <div className="relative">
                <input
                  type="text"
                  value={rut}
                  readOnly
                  disabled
                  tabIndex={-1}
                  aria-label="RUT afiliado validado"
                  className="w-full cursor-not-allowed rounded-lg border border-gray-300 bg-gray-100 px-3 py-2.5 pr-10 text-sm text-gray-600 outline-none"
                />
                <FaCheckCircle className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#16A34A]" aria-hidden />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={label}>Nombres *</label>
                <input className={field} {...register('firstname', { required: true })} />
                {errors.firstname && <p className="mt-1 text-xs text-red-600">Requerido</p>}
              </div>
              <div>
                <label className={label}>Apellidos *</label>
                <input className={field} {...register('lastname', { required: true })} />
                {errors.lastname && <p className="mt-1 text-xs text-red-600">Requerido</p>}
              </div>
            </div>

            <div>
              <label className={label}>Correo electrónico *</label>
              <input
                type="email"
                readOnly
                className={`${field} cursor-not-allowed bg-gray-100 text-gray-600`}
                {...register('email', { required: true })}
              />
              <p className="mt-1 text-xs text-gray-400">Correo de tu inscripción (no editable).</p>
            </div>

            <div>
              <label className={label}>Teléfono con WhatsApp *</label>
              <div className="flex gap-2">
                <select
                  value={dialCode}
                  onChange={(e) => setDialCode(e.target.value)}
                  className="w-[92px] shrink-0 rounded-lg border border-gray-300 py-2.5 pl-2 pr-1 text-sm text-gray-800 outline-none focus:border-[#1A73E8] focus:ring-2 focus:ring-[#1A73E8]/20"
                  aria-label="Código de país"
                >
                  {DIAL_CODES.map((d) => (
                    <option key={d.code} value={d.code}>{d.label}</option>
                  ))}
                </select>
                <input type="tel" placeholder="9 1234 5678" className={`${field} min-w-0 flex-1`} {...register('phone', { required: true })} />
              </div>
            </div>

            {/* Resto de campos (reciclados del CRM) */}
            {FIELDS.map((f) => {
              if (!isVisible(f)) return null;

              if (f.kind === 'checkbox') {
                return (
                  <div key={f.key}>
                    <label className="flex items-start gap-2 rounded-lg bg-gray-50 px-3 py-2.5 text-xs leading-relaxed text-gray-600">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 shrink-0 accent-[#1A73E8]"
                        {...register(f.key, { required: f.required })}
                      />
                      <span>{f.label}{f.required ? ' *' : ''}</span>
                    </label>
                    {errors[f.key] && <p className="text-xs text-red-600">Debes aceptar para continuar.</p>}
                  </div>
                );
              }

              return (
                <div key={f.key}>
                  <label className={label}>{f.label}{f.required ? ' *' : ''}</label>
                  {f.kind === 'select' ? (
                    <select className={field} defaultValue="" {...register(f.key, { required: f.required })}>
                      <option value="" disabled>-- Seleccionar --</option>
                      {(f.options || []).map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  ) : f.kind === 'file' ? (
                    <>
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.webp"
                        className={`${field} py-2 file:mr-3 file:rounded file:border-0 file:bg-[#1A73E8]/10 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-[#1A73E8]`}
                        {...register(f.key, { required: f.required })}
                      />
                      {(() => {
                        // Aviso del límite siempre visible, y si el archivo elegido
                        // se pasa, se dice su peso real y qué va a ocurrir.
                        const elegido = (watch(f.key) as FileList | undefined)?.[0];
                        const excede = !!elegido && elegido.size > MAX_FILE_BYTES;
                        const esImagen = !!elegido && elegido.type.startsWith('image/');
                        if (excede && esImagen) {
                          return (
                            <p className="mt-1 text-xs text-amber-700">
                              Pesa {mb(elegido!.size)} MB (máximo {MAX_FILE_MB} MB): la reduciremos automáticamente al enviar.
                            </p>
                          );
                        }
                        if (excede) {
                          return (
                            <p className="mt-1 text-xs font-semibold text-red-600">
                              Pesa {mb(elegido!.size)} MB y el máximo es {MAX_FILE_MB} MB. Sube un archivo más liviano.
                            </p>
                          );
                        }
                        return (
                          <p className="mt-1 text-xs text-gray-500">
                            PDF, JPG, PNG o WEBP · hasta {MAX_FILE_MB} MB
                            {elegido ? ` · seleccionado: ${mb(elegido.size)} MB` : ''}
                          </p>
                        );
                      })()}
                    </>
                  ) : f.kind === 'date' ? (
                    <>
                      <input
                        type="date"
                        min={f.key === 'fecha_de_nacimiento' ? NAC_MIN : undefined}
                        max={f.key === 'fecha_de_nacimiento' ? NAC_MAX : undefined}
                        className={field}
                        {...register(f.key, { required: f.required })}
                      />
                      {f.key === 'fecha_de_nacimiento' &&
                        (() => {
                          const edad = edadCumplida(String(watch(f.key) || ''));
                          if (edad !== null && edad < EDAD_MINIMA) {
                            return <p className="mt-1 text-xs font-semibold text-red-600">{MSG_MAYOR_EDAD}</p>;
                          }
                          return <p className="mt-1 text-xs text-gray-500">{MSG_MAYOR_EDAD}</p>;
                        })()}
                    </>
                  ) : (
                    <input type="text" className={field} {...register(f.key, { required: f.required })} />
                  )}
                  {errors[f.key] && <p className="mt-1 text-xs text-red-600">Requerido</p>}
                </div>
              );
            })}

            {errorMsg && (
              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">{errorMsg}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-[#1A73E8] px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-[#1A73E8]/25 transition hover:bg-[#155ec2] disabled:opacity-60"
            >
              {loading ? 'Procesando…' : `Continuar al pago (${COPAGO})`}
            </button>
            <p className="text-center text-xs text-gray-400">Pago seguro con Webpay (Transbank).</p>
          </form>
        </div>
      </div>
    </div>
  );
};
